import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category/movie' },
  { label: '电视剧', to: '/category/tv' },
  { label: '搜索', to: '/search' },
  { label: '关于', to: '/about' },
];

export default function Navbar() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const isHome = pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname, search]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const solid = scrolled || !isHome || menuOpen;

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword('');
    setMenuOpen(false);
  };

  const isActive = (to: string) => (to === '/' ? pathname === '/' : pathname.startsWith(to));

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid ? 'bg-background-50/95 backdrop-blur border-b border-background-200' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
          <span className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary-500 flex items-center justify-center text-background-50">
            <i className="ri-play-fill text-xl md:text-2xl"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading font-extrabold text-lg md:text-xl text-foreground-50 tracking-tight">
              香蕉视频<span className="text-primary-400">app</span>
            </span>
            <span className="text-[10px] text-foreground-400 mt-0.5 hidden sm:block">高清影视 · 一触即达</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive(item.to) ? 'text-primary-400' : 'text-foreground-200 hover:text-primary-300'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form
            onSubmit={handleSearch}
            className="hidden lg:flex items-center bg-background-100 border border-background-300 rounded-full pl-4 pr-1 h-10 w-56 xl:w-72"
          >
            <i className="ri-search-line text-foreground-400 text-base"></i>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影片、演员"
              aria-label="站内搜索"
              className="flex-1 min-w-0 bg-transparent text-sm text-foreground-100 placeholder:text-foreground-500 px-2 outline-none"
            />
            <button
              type="submit"
              aria-label="搜索"
              className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center cursor-pointer whitespace-nowrap shrink-0"
            >
              <i className="ri-arrow-right-line"></i>
            </button>
          </form>

          <button
            type="button"
            onClick={() => setMenuOpen((value) => !value)}
            aria-label="打开菜单"
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-md bg-background-100 border border-background-300 text-foreground-100 cursor-pointer"
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
      </div>

      {menuOpen ? (
        <div className="md:hidden bg-background-50 border-t border-background-200">
          <div className="px-4 py-4 flex flex-col gap-3">
            <form
              onSubmit={handleSearch}
              className="flex items-center bg-background-100 border border-background-300 rounded-full pl-4 pr-1 h-11"
            >
              <i className="ri-search-line text-foreground-400 text-base"></i>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影片、演员"
                aria-label="站内搜索"
                className="flex-1 min-w-0 bg-transparent text-sm text-foreground-100 placeholder:text-foreground-500 px-2 outline-none"
              />
              <button
                type="submit"
                aria-label="搜索"
                className="w-9 h-9 rounded-full bg-primary-500 text-background-50 flex items-center justify-center cursor-pointer whitespace-nowrap shrink-0"
              >
                <i className="ri-arrow-right-line"></i>
              </button>
            </form>
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-4 py-3 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                  isActive(item.to)
                    ? 'bg-primary-950/50 text-primary-400'
                    : 'text-foreground-200 hover:bg-background-100'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </header>
  );
}